import { Link, useLocation, useMatchRoute } from '@tanstack/react-router'
import { Avatar, AvatarFallback, AvatarImage } from '@workspace/ui/components/avatar'
import { cn, getInitials } from '@workspace/ui/lib/utils'
import { LogOut, Menu, Mic, Settings, User, Wallet, X } from 'lucide-react'
import { useEffect, useState } from 'react'
import { adminNavItems, investorTabs, startupTabs } from '@/components/nav-items'
import { useIsAdmin } from '@/hooks/use-user-type'
import { auth } from '@/lib/auth'

interface MobileNavProps {
  startup?: { name: string; logoUrl?: string | null } | null
  investor?: { name: string; logoUrl?: string | null } | null
  user?: { name: string; email: string; avatar: string } | null
  creditBalance?: number
  isAdminRoute: boolean
}

export function MobileNav({
  startup,
  investor,
  user,
  creditBalance,
  isAdminRoute,
}: MobileNavProps) {
  const [isOpen, setIsOpen] = useState(false)
  const matchRoute = useMatchRoute()

  // Prevent body scroll when menu is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [isOpen])
  const pathname = useLocation({ select: (l) => l.pathname })
  const isAdmin = useIsAdmin()
  const isCreditsActive = matchRoute({ to: '/credits', fuzzy: false })
  const hasOrg = !!startup || !!investor

  const handleSignOut = async () => {
    await auth.signOut()
    window.location.href = '/'
  }

  const closeMenu = () => setIsOpen(false)

  const navItems = isAdminRoute
    ? adminNavItems
    : startup
      ? startupTabs
      : investor
        ? investorTabs
        : []

  return (
    <>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center justify-center p-2 text-muted-foreground hover:text-foreground transition-colors"
        aria-label={isOpen ? 'Close menu' : 'Open menu'}
      >
        {isOpen ? <X className="size-5" /> : <Menu className="size-5" />}
      </button>

      {/* Dropdown menu below header */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <button
            type="button"
            className="fixed inset-0 top-16 z-40 bg-black/50 cursor-default"
            onClick={closeMenu}
            aria-label="Close menu"
          />

          {/* Menu content */}
          <div className="fixed inset-x-0 top-16 z-50 bg-background border-b border-border shadow-lg animate-in fade-in-0 duration-150">
            <div className="container max-w-6xl mx-auto px-4">
              <div className="flex flex-col max-h-[calc(100vh-4rem)] overflow-y-auto pb-4">
                {/* Organization info + Navigation (non-admin routes only) */}
                {!isAdminRoute && (startup || investor) && (
                  <>
                    <div className="flex items-center gap-3 mb-4">
                      <Avatar className="size-10 rounded-full">
                        {startup?.logoUrl && (
                          <AvatarImage src={startup.logoUrl} alt={startup.name} />
                        )}
                        {investor?.logoUrl && (
                          <AvatarImage src={investor.logoUrl} alt={investor.name} />
                        )}
                        <AvatarFallback className="rounded-full bg-primary text-primary-foreground text-sm font-medium">
                          {getInitials(startup?.name || investor?.name || '')}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex flex-col">
                        <span className="font-medium text-sm">
                          {startup?.name || investor?.name}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {startup ? 'Startup' : 'Investor'}
                        </span>
                      </div>
                    </div>
                    <nav className="flex flex-col gap-1">
                      {(() => {
                        const isProfileActive = matchRoute({ to: '/', fuzzy: false })
                        return (
                          <Link
                            to="/"
                            onClick={closeMenu}
                            className={cn(
                              'flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors',
                              isProfileActive
                                ? 'bg-[#f97316]/10 text-[#f97316]'
                                : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                            )}
                          >
                            Profile
                          </Link>
                        )
                      })()}
                      {navItems.map((item) => {
                        const isActive = matchRoute({ to: item.to, fuzzy: false })
                        return (
                          <Link
                            key={item.to}
                            to={item.to}
                            onClick={closeMenu}
                            className={cn(
                              'flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors',
                              isActive
                                ? 'bg-[#f97316]/10 text-[#f97316]'
                                : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                            )}
                          >
                            {item.label}
                          </Link>
                        )
                      })}
                    </nav>
                  </>
                )}

                {/* Admin Navigation items */}
                {isAdminRoute && (
                  <nav className="flex flex-col gap-1">
                    <Link
                      to="/"
                      onClick={closeMenu}
                      className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
                    >
                      ← Back to App
                    </Link>
                    <div className="mb-2 pb-2 border-b border-border" />
                    {adminNavItems.map((item) => {
                      const isActive = pathname.startsWith(item.to)
                      return (
                        <Link
                          key={item.to}
                          to={item.to}
                          onClick={closeMenu}
                          className={cn(
                            'flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors',
                            isActive
                              ? 'bg-[#f97316]/10 text-[#f97316]'
                              : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                          )}
                        >
                          {item.label}
                        </Link>
                      )
                    })}
                  </nav>
                )}

                {/* Credits */}
                {!isAdminRoute && hasOrg && (
                  <div className="pt-2 mt-2 border-t border-border">
                    <Link
                      to="/credits"
                      onClick={closeMenu}
                      className={cn(
                        'flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors',
                        isCreditsActive
                          ? 'bg-[#f97316]/10 text-[#f97316]'
                          : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                      )}
                    >
                      <Wallet className="size-4" />
                      <span>Credits</span>
                      <span className="ml-auto font-semibold">
                        {creditBalance !== undefined ? creditBalance : '–'}
                      </span>
                    </Link>
                  </div>
                )}

                {/* Pitch Now button for startups */}
                {!isAdminRoute && startup && (
                  <div className="mt-2 pt-2 border-t border-border">
                    <Link
                      to="/pitches/new"
                      onClick={closeMenu}
                      className="flex items-center justify-center gap-2 rounded-lg px-3 py-2.5 text-sm font-bold uppercase bg-[#f97316] text-white shadow-xs transition-colors hover:bg-[#fb923c]"
                    >
                      <Mic className="size-4" />
                      Pitch Now
                    </Link>
                  </div>
                )}

                {/* User section */}
                {user && (
                  <div className="border-t border-border mt-2 pt-4">
                    <div className="flex items-center gap-3 mb-4">
                      <Avatar className="size-10 rounded-full">
                        <AvatarImage src={user.avatar} alt={user.name} />
                        <AvatarFallback className="rounded-full text-sm">
                          {getInitials(user.name)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex flex-col min-w-0">
                        <span className="font-medium text-sm truncate">{user.name}</span>
                        <span className="text-xs text-muted-foreground truncate">{user.email}</span>
                      </div>
                    </div>

                    <div className="flex flex-col gap-1">
                      {isAdmin && !isAdminRoute && (
                        <Link
                          to="/admin/evaluations"
                          onClick={closeMenu}
                          className="flex items-center gap-3 rounded-lg px-3 py-2 text-sm text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
                        >
                          <Settings className="size-4" />
                          Admin
                        </Link>
                      )}
                      {(() => {
                        const isUserProfileActive = matchRoute({ to: '/profile', fuzzy: false })
                        return (
                          <Link
                            to="/profile"
                            onClick={closeMenu}
                            className={cn(
                              'flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors',
                              isUserProfileActive
                                ? 'bg-[#f97316]/10 text-[#f97316]'
                                : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                            )}
                          >
                            <User className="size-4" />
                            Profile
                          </Link>
                        )
                      })()}
                      <button
                        type="button"
                        onClick={handleSignOut}
                        className="flex items-center gap-3 rounded-lg px-3 py-2 text-sm text-red-500 hover:bg-red-500/10 transition-colors w-full text-left"
                      >
                        <LogOut className="size-4" />
                        Sign Out
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </>
  )
}
